import { Link } from "react-router-dom";
import { useTranslations } from "@/lib/useTranslations";

import { ArticleEditorialChecklistItems } from "@/components/ui/admin/articles/ArticleEditorialChecklistItems";
import type { ArticleRedactionListItem } from "@/core/services/types/article.types";
import {
  getArticleRedactionChecklist,
  getArticleSharePath,
} from "@/core/services/types/article.types";

interface ArticlesRedactionPanelProps {
  items: ArticleRedactionListItem[];
  isRefreshing?: boolean;
}

function ReviewStatusBadge() {
  const t = useTranslations("articles.library.statuses");

  return (
    <span className="inline-flex shrink-0 rounded-xl bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700">
      {t("PENDING_REVIEW")}
    </span>
  );
}

function ArticleRedactionCard({ article }: { article: ArticleRedactionListItem }) {
  const t = useTranslations("articles.redaction");
  const href = getArticleSharePath(article.id);
  const checklist = getArticleRedactionChecklist(article);

  return (
    <Link
      to={href}
      className="flex h-full flex-col gap-4 rounded-xl border border-black/10 bg-white p-5 shadow-sm transition-colors hover:bg-zinc-50 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-whart-dark"
      aria-label={t("openArticle", { title: article.title })}
    >
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-base font-semibold text-whart-text">{article.title}</h3>
        <ReviewStatusBadge />
      </div>

      <ArticleEditorialChecklistItems checklist={checklist} showLabel={false} />
    </Link>
  );
}

export function ArticlesRedactionPanel({
  items,
  isRefreshing = false,
}: ArticlesRedactionPanelProps) {
  const t = useTranslations("articles.redaction");

  return (
    <section
      className="wireframe-border bg-whart-bg"
      aria-labelledby="articles-redaction-assigned-label"
    >
      <div className="border-b border-black/10 px-5 py-4">
        <div className="flex items-center justify-between gap-3">
          <h2
            id="articles-redaction-assigned-label"
            className="text-base font-semibold text-whart-text"
          >
            {t("assignedReviewTitle")}
          </h2>
          {isRefreshing ? (
            <span
              aria-label={t("loading")}
              className="inline-block size-3.5 animate-spin rounded-full border-2 border-zinc-900 border-r-transparent"
            />
          ) : null}
        </div>
        <p className="mt-1 text-sm text-neutral-500">{t("assignedReviewDescription")}</p>
      </div>

      {items.length === 0 ? (
        <p className="px-5 py-10 text-center text-sm text-neutral-500">
          {t("assignedReviewEmpty")}
        </p>
      ) : (
        <ul className="grid gap-4 p-5 sm:grid-cols-2">
          {items.map((article) => (
            <li key={article.id}>
              <ArticleRedactionCard article={article} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
